import { Box, Typography, useTheme } from "@mui/material";
import * as d3 from "d3";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";


const wrapAxisLabel = (selection, maxWidth) => {
    selection.each(function () {
        const text = d3.select(this);
        const words = text.text().split(/\s+/).filter(Boolean);
        if (!words.length) return;

        const x = text.attr("x") || 0;
        const dy = parseFloat(text.attr("dy")) || 0;
        text.text(null);

        const ruler = text.append("tspan").style("visibility", "hidden");
        const widthOf = (value) => {
            ruler.text(value);
            return ruler.node().getComputedTextLength();
        };

        const hyphenate = (word) => {
            if (widthOf(word) <= maxWidth) return [word];

            const pieces = [];
            let piece = "";
            for (const char of word) {
                if (piece && widthOf(`${piece}${char}-`) > maxWidth) {
                    pieces.push(`${piece}-`);
                    piece = char;
                } else {
                    piece += char;
                }
            }
            if (piece) pieces.push(piece);
            return pieces;
        };

        const lines = [];
        let line = "";

        words.forEach(word => {
            const pieces = hyphenate(word);
            pieces.forEach((piece, index) => {
                const candidate = line ? `${line} ${piece}` : piece;
                if (line && widthOf(candidate) > maxWidth) {
                    lines.push(line);
                    line = piece;
                } else {
                    line = candidate;
                }
                if (index < pieces.length - 1) {
                    lines.push(line);
                    line = "";
                }
            });
        });
        if (line) lines.push(line);

        ruler.remove();

        lines.forEach((content, index) => {
            text.append("tspan")
                .attr("x", x)
                .attr("dy", index === 0 ? `${dy}em` : "1.1em")
                .text(content);
        });
    });
};

const BAR_COLOR = "#005cb9";

const BarChart = ({ data = [], orientation = "vertical", height = 260, limit, mirrored = false, colors, showLegend = false, bottomMargin = 40, leftMargin = 40, fillHeight = false, onSelect, selectedKey = null, labelMaxWidth = 70 }) => {
    const theme = useTheme();
    const containerRef = useRef(null);
    const plotRef = useRef(null);
    const svgRef = useRef(null);


    const onSelectRef = useRef(onSelect);
    useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

    useEffect(() => {
        const container = containerRef.current;
        const plotEl = plotRef.current;
        if (!container || !plotEl) return;

        const draw = () => {
            const width = container.clientWidth;
            const chartHeight = fillHeight ? (plotEl.clientHeight || height) : height;
            if (!width || !chartHeight) return;

            const svg = d3.select(svgRef.current)
                .attr("width", width)
                .attr("height", chartHeight);
            svg.selectAll("*").remove();

            if (!data.length) return;

            const chartData = limit
                ? [...data].sort((a, b) => b.value - a.value).slice(0, limit)
                : data;

            const margin = orientation === "horizontal"
                ? (mirrored
                    ? { top: 8, right: 160, bottom: 8, left: 8 }
                    : { top: 8, right: 32, bottom: 8, left: 140 })
                : { top: 24, right: 8, bottom: bottomMargin, left: leftMargin };

            const innerWidth = width - margin.left - margin.right;
            const innerHeight = chartHeight - margin.top - margin.bottom;

            const g = svg.append("g")
                .attr("transform", `translate(${margin.left},${margin.top})`);

            const barColor = BAR_COLOR;
            const maxValue = d3.max(chartData, d => d.value) || 0;

            const interactive = Boolean(onSelectRef.current);
            const attachSelect = (selection) => selection
                .style("cursor", interactive ? "pointer" : null)
                .attr("opacity", d => (selectedKey && d.key !== selectedKey ? 0.3 : 1))
                .on("click", interactive ? (event, d) => onSelectRef.current(d) : null);

            if (orientation === "horizontal") {
                const y = d3.scaleBand()
                    .domain(chartData.map(d => d.label))
                    .range([0, innerHeight])
                    .padding(0.15);
                const x = d3.scaleLinear()
                    .domain([0, maxValue]).nice()
                    .range(mirrored ? [innerWidth, 0] : [0, innerWidth]);

                if (!mirrored) {
                    g.append("g")
                        .call(d3.axisLeft(y).tickSize(0))
                        .call(sel => sel.select(".domain").remove())
                        .selectAll("text")
                        .style("font-size", "11px")
                        .style("fill", theme.palette.text.primary);
                }

                g.selectAll(".bar")
                    .data(chartData)
                    .join("rect")
                    .attr("class", "bar")
                    .attr("y", d => y(d.label))
                    .attr("x", d => mirrored ? x(d.value) : 0)
                    .attr("height", y.bandwidth())
                    .attr("width", d => mirrored ? innerWidth - x(d.value) : x(d.value))
                    .attr("fill", (d, i) => d.color || colors?.[i % colors.length] || barColor)
                    .attr("rx", 2)
                    .call(attachSelect);

                g.selectAll(".bar-label")
                    .data(chartData)
                    .join("text")
                    .attr("class", "bar-label")
                    .attr("x", d => mirrored ? x(d.value) + 8 : x(d.value) - 8)
                    .attr("y", d => y(d.label) + y.bandwidth() / 2)
                    .attr("dy", "0.35em")
                    .style("text-anchor", mirrored ? "start" : "end")
                    .style("font-size", "11px")
                    .style("fill", theme.palette.primary.contrastText)
                    .style("pointer-events", "none")
                    .text(d => d.value);

                if (mirrored) {
                    g.selectAll(".category-label")
                        .data(chartData)
                        .join("text")
                        .attr("class", "category-label")
                        .attr("x", innerWidth + 8)
                        .attr("y", d => y(d.label) + y.bandwidth() / 2)
                        .attr("dy", "0.35em")
                        .style("text-anchor", "start")
                        .style("font-size", "11px")
                        .style("fill", theme.palette.text.primary)
                        .style("pointer-events", "none")
                        .text(d => d.label);
                    const tip = svg.selectAll(".category-tooltip").data([null]).join("g")
                        .attr("class", "category-tooltip")
                        .style("display", "none")
                        .style("pointer-events", "none");
                    const tipBox = tip.selectAll("rect").data([null]).join("rect")
                        .attr("rx", 3)
                        .attr("fill", "rgba(97, 97, 97, 0.92)");
                    const tipText = tip.selectAll("text").data([null]).join("text")
                        .attr("dy", "0.35em")
                        .style("font-size", "11px")
                        .style("fill", "#fff");

                    g.selectAll(".category-hit")
                        .data(chartData)
                        .join("rect")
                        .attr("class", "category-hit")
                        .attr("x", innerWidth + 8)
                        .attr("y", d => y(d.label))
                        .attr("width", Math.max(0, margin.right - 8))
                        .attr("height", y.bandwidth())
                        .attr("fill", "transparent")
                        .style("pointer-events", "all")
                        .on("mouseenter", (_, d) => {
                            tip.style("display", null);
                            tipText.text(d.label);
                            const bounds = tipText.node().getBBox();
                            const boxWidth = bounds.width + 12;
                            const boxHeight = bounds.height + 8;
                            const labelLeft = margin.left + innerWidth + 8;
                            const rowCenter = margin.top + y(d.label) + y.bandwidth() / 2;
                            const boxX = Math.max(0, labelLeft - 8 - boxWidth);
                            const boxY = Math.max(0, Math.min(chartHeight - boxHeight, rowCenter - boxHeight / 2));

                            tipBox.attr("x", boxX).attr("y", boxY).attr("width", boxWidth).attr("height", boxHeight);
                            tipText.attr("x", boxX + 6).attr("y", boxY + boxHeight / 2);
                        })
                        .on("mouseleave", () => tip.style("display", "none"));
                }
            } else {
                const x = d3.scaleBand()
                    .domain(chartData.map(d => d.label))
                    .range([0, innerWidth])
                    .padding(0.25);
                const y = d3.scaleLinear()
                    .domain([0, maxValue]).nice()
                    .range([innerHeight, 0]);

                const barWidth = Math.min(x.bandwidth(), 72);

                g.append("g")
                    .attr("transform", `translate(0,${innerHeight})`)
                    .call(d3.axisBottom(x).tickSize(0))
                    .call(sel => sel.select(".domain").remove())
                    .selectAll("text")
                    .style("font-size", "11px")
                    .style("fill", theme.palette.text.primary)
                    .style("text-anchor", "middle")
                    .call(wrapAxisLabel, labelMaxWidth);

                g.selectAll(".bar")
                    .data(chartData)
                    .join("rect")
                    .attr("class", "bar")
                    .attr("x", d => x(d.label) + (x.bandwidth() - barWidth) / 2)
                    .attr("y", d => y(d.value))
                    .attr("width", barWidth)
                    .attr("height", d => innerHeight - y(d.value))
                    .attr("fill", (d, i) => d.color || colors?.[i % colors.length] || barColor)
                    .attr("rx", 2)
                    .call(attachSelect);

                g.selectAll(".bar-label")
                    .data(chartData)
                    .join("text")
                    .attr("class", "bar-label")
                    .attr("x", d => x(d.label) + x.bandwidth() / 2)
                    .attr("y", d => y(d.value) - 6)
                    .style("font-size", "11px")
                    .style("text-anchor", "middle")
                    .style("fill", theme.palette.text.primary)
                    .style("pointer-events", "none")
                    .text(d => d.value);
            }
        };

        draw();

        let frame = null;
        let lastWidth = container.clientWidth;
        let lastHeight = plotEl.clientHeight;

        const resizeObserver = new ResizeObserver(() => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = null;
                const width = container.clientWidth;
                const plotHeight = plotEl.clientHeight;
                if (width === lastWidth && (!fillHeight || plotHeight === lastHeight)) return;
                lastWidth = width;
                lastHeight = plotHeight;
                draw();
            });
        });
        resizeObserver.observe(container);
        if (fillHeight) resizeObserver.observe(plotEl);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            resizeObserver.disconnect();
        };
    }, [data, orientation, height, limit, mirrored, colors, bottomMargin, leftMargin, fillHeight, theme, selectedKey, labelMaxWidth]);

    return (
        <div
            ref={containerRef}
            style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                ...(fillHeight ? { flex: 1, minHeight: 0 } : {})
            }}
        >
            {showLegend && (
                <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                    {data.map((d, i) => (
                        <Box key={d.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: d.color || colors?.[i % colors.length] || BAR_COLOR
                            }} />
                            <Typography variant="caption" color="text.secondary">{d.label}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <div ref={plotRef} style={{ width: "100%", ...(fillHeight ? { flex: 1, minHeight: 0 } : {}) }}>
                <svg ref={svgRef} />
            </div>
        </div>
    );
};

BarChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.number,
        color: PropTypes.string,
    })),
    orientation: PropTypes.oneOf(["vertical", "horizontal"]),
    height: PropTypes.number,
    limit: PropTypes.number,
    mirrored: PropTypes.bool,
    colors: PropTypes.arrayOf(PropTypes.string),
    showLegend: PropTypes.bool,
    bottomMargin: PropTypes.number,
    leftMargin: PropTypes.number,
    fillHeight: PropTypes.bool,
    onSelect: PropTypes.func,
    selectedKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    labelMaxWidth: PropTypes.number,
};

export default BarChart;
